import { useState } from 'react';
import { downloadReportExport } from '@/api/reports';
import type { MonopolyPayload } from '../report';
import { MONOPOLY_TABS, type MonopolyTab } from '../utils';
import type { SwitchOption } from '@/features/dashboard/config';
import { ReportOptions } from '@/features/dashboard/components/ReportOptions';

type Props = {
  campaign: MonopolyPayload['campaign'];
  /** The payload's own refresh stamp, formatted HH:MM:SS — never a client clock. */
  updatedLabel: string;
  isPreview: boolean;
  tab: MonopolyTab;
  onTab: (t: MonopolyTab) => void;
  reportId: string;
  baseUrl: string;
  switchOptions: SwitchOption[];
  onSwitch: (path: string) => void;
  onSignOut: () => void;
  isAdmin: boolean;
  onManageUsers: () => void;
  /** Opens the winners page — a page of its own, so it is a link, not a tab. */
  onWinners: () => void;
};

/*
 * Blue gradient header from the design: brand lockup, live pill, period, export,
 * then the tab strip whose active tab merges into the page ground.
 */
export function Header({
  campaign,
  updatedLabel,
  isPreview,
  tab,
  onTab,
  reportId,
  baseUrl,
  switchOptions,
  onSwitch,
  onSignOut,
  isAdmin,
  onManageUsers,
  onWinners,
}: Props) {
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  async function onExport() {
    setExporting(true);
    setExportError(null);
    try {
      await downloadReportExport(reportId, baseUrl);
    } catch {
      // The endpoint answers 404 until the pipeline ships an exportMatrix.
      setExportError('Export non disponible');
    } finally {
      setExporting(false);
    }
  }

  const live = campaign.live === true && !isPreview;

  return (
    <header className="mono-header">
      <div className="mono-header-row">
        <div className="mono-brand">
          <div className="mono-brand-mark" aria-hidden="true">
            M
          </div>
          <div>
            <h1 className="mono-brand-title">{campaign.name}</h1>
            <div className="mono-brand-sub">{campaign.subtitle}</div>
          </div>
        </div>

        <div className="mono-header-actions">
          {isPreview ? (
            <span className="mono-preview-pill" title="Aucune donnée réelle pour l'instant — données d'exemple ou vides">
              Données d&apos;exemple
            </span>
          ) : (
            <div className="mono-live" aria-live="polite">
              <span className={`mono-live-dot${live ? '' : ' off'}`} aria-hidden="true" />
              <span>{live ? 'En direct' : 'En pause'}</span>
              <span className="mono-live-soft">· actualisé à {updatedLabel}</span>
            </div>
          )}
          <div className="mono-period">
            <span className="k">Période</span>
            <span className="v">{campaign.periodStart}</span>
            <span className="arrow" aria-hidden="true">
              →
            </span>
            <span className="v">{campaign.periodEnd}</span>
          </div>
          {exportError && <span className="mono-export-error">{exportError}</span>}
          <button type="button" className="mono-export" onClick={onExport} disabled={exporting} aria-label="Exporter vers Excel">
            <DownloadIcon />
            <span>{exporting ? 'Export en cours…' : 'Exporter'}</span>
          </button>
          {switchOptions.length > 0 && (
            <select className="mono-switch" value="" onChange={(e) => e.target.value && onSwitch(e.target.value)} aria-label="Changer de rapport">
              <option value="">Rapport…</option>
              <ReportOptions options={switchOptions} labels={{ active: 'En cours', archived: 'Archivés' }} />
            </select>
          )}
          {isAdmin && (
            <button type="button" className="mono-signout" onClick={onManageUsers}>
              Utilisateurs
            </button>
          )}
          <button type="button" className="mono-signout" onClick={onSignOut}>
            Déconnexion
          </button>
        </div>
      </div>

      <div className="mono-tabs">
        <div className="mono-tabs-list" role="tablist" aria-label="Sections du tableau de bord">
          {MONOPOLY_TABS.map((t) => (
            <button key={t.id} type="button" role="tab" aria-selected={tab === t.id} className="mono-tab" onClick={() => onTab(t.id)}>
              {t.label}
            </button>
          ))}
        </div>
        {/* A LINK, not a tab: it leaves the dashboard for a page of its own, so
            it sits apart from the tab strip and never takes the selected state. */}
        <button type="button" className="mono-tabs-link" onClick={onWinners}>
          Gagnants et lots <span aria-hidden="true">→</span>
        </button>
      </div>
    </header>
  );
}

/** The design pulls IconDownload24 from its design system; inline is lighter. */
function DownloadIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 4v11" />
      <path d="M7 10l5 5 5-5" />
      <path d="M4 19h16" />
    </svg>
  );
}
